import React, { useEffect, useState } from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, Avatar, Typography, Divider, useTheme, ButtonBase, IconButton } from '@mui/material';
// Import necessary Material-UI icons
import HomeIcon from '@mui/icons-material/Home';
import NotesIcon from '@mui/icons-material/Note';
import StatsIcon from '@mui/icons-material/BarChart';
// import SettingsIcon from '@mui/icons-material/Settings';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import MenuIcon from '@mui/icons-material/Menu';
import SecurityIcon from '@mui/icons-material/Security';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { Scrollbar } from 'react-scrollbars-custom';

import { useSocket } from '../../SocketContext';
import { useAuth } from '../../hooks/useAuth';
import { decodeJWT } from '../../utils/commonUtils';

const SidePanel = ({ sidePanelOpen, togglePanel }) => {
    const { isOnline } = useSocket();
    const { authData } = useAuth();
    let userDetails = null;

    // Decode the access token to extract user information
    if (authData?.accessToken) {
        try {
            userDetails = decodeJWT(authData.accessToken);
        } catch (error) {
            console.error("Error decoding access token:", error);
            // Optionally handle invalid tokens here
        }
    }

    const [time, setTime] = useState(new Date());
    const theme = useTheme();

    // Update time every minute
    useEffect(() => {
        const timer = setInterval(() => {
            setTime(new Date());
        }, 60000); // Update every minute
        return () => clearInterval(timer);
    }, []);

    // Custom date formatting
    const formattedDate = time.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    }).replace(/ /g, '-'); // Replace spaces with dashes

    const formattedTime = time.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });

    const menuItems = {
        "home": {
            "label": "Home",
            "path": "/",
            "icon": <HomeIcon />
        },
        "bookmarks": {
            "label": "Bookmarks",
            "path": "/bookmarks",
            "icon": <NotesIcon />
        },
        // "search": {
        //     "label": "Search",
        //     "path": "/search",
        //     "icon": <StatsIcon />
        // },
        // "settings": {
        //     "label": "Settings",
        //     "path": "/settings",
        //     "icon": <SettingsIcon />
        // },
        "support": {
            "label": "Support",
            "path": "/support",
            "icon": <ContactMailIcon />
        },
        "faq": {
            "label": "FAQs",
            "path": "/faq",
            "icon": <ContactSupportOutlinedIcon />
        },
        "terms": {
            "label": "Terms",
            "path": "/terms",
            "icon": <GavelOutlinedIcon />
        },
        "privacy": {
            "label": "Privacy",
            "path": "/privacy",
            "icon": <SecurityIcon />
        }
    };


    return (
        <Box
            sx={{
                display: 'flex',
                position: 'relative',
                borderRight: 1,
                borderColor: 'divider',
            }}
        >
            {/* Side Panel */}
            <Box
                sx={{
                    width: sidePanelOpen ? '220px' : '16px', // Set the width to 0 when closed
                    transition: 'all 0.5s ease', // Smooth transition
                    opacity: sidePanelOpen ? 1 : 0, // Fade out when panel closes
                    transform: sidePanelOpen ? 'translateX(0)' : 'translateX(-100%)',
                    // backgroundColor: "background.paper",
                    // whiteSpace: 'nowrap', // Prevent text wrapping
                    display: 'flex',
                    flexDirection: 'column',
                    // color: "text.primary",
                    paddingTop: '12px',
                }}
            >
                {/* Fading content */}
                <Box
                    sx={{
                        pointerEvents: sidePanelOpen ? 'auto' : 'none', // Disable interactions when closed
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        {/* Circular Online Indicator */}
                        <Avatar
                            sx={{
                                width: 64,
                                height: 64,
                                margin: '0 auto',
                                background: `radial-gradient(circle, ${theme.palette.primary.main} 30%, transparent 70%)`,
                                animation: isOnline ? 'pulse 2s infinite' : 'none', // Only pulse when online
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: isOnline ? theme.palette.success.main : theme.palette.warning.dark,
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    bottom: '4px',
                                    right: '4px',
                                    boxShadow: isOnline ? `0px 0px 10px ${theme.palette.success.main}` : 'none',
                                    transition: 'background-color 0.3s, box-shadow 0.3s', // Smooth transition
                                },
                                '&:hover': {
                                    // Optional: Scale up the avatar slightly on hover for a nice effect
                                    transform: isOnline ? 'scale(1.05)' : 'scale(1)',
                                    transition: 'transform 0.2s',
                                },
                            }}
                            src={userDetails?.profile_picture || ''}
                        />

                        {/* User Name */}
                        <Typography
                            variant="h6"
                            sx={{
                                mt: 1,
                                fontWeight: 'bold',
                                // color: theme.palette.text.primary,
                                animation: 'fadeIn 1.5s ease-in-out',
                            }}
                        >
                            {userDetails?.name}
                        </Typography>

                        {/* Date and Time */}
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.9rem',
                                mt: 0.5,
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif', // Use a cleaner font
                                color: 'text.secondary', // Use a theme-based text color
                                letterSpacing: '0.02em', // Subtle spacing for readability
                                fontWeight: 400, // Ensure it doesn't appear too bold
                            }}
                        >
                            {formattedDate} - {formattedTime}
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Menu List */}
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >

                        <Scrollbar
                            style={{
                                flexGrow: 1,
                            }}
                            trackYProps={{
                                style: {
                                    opacity: 0.3,  // Make it slightly visible initially
                                    transition: 'opacity 0.3s',
                                    width: '4px',
                                    borderRadius: '4px',
                                }
                            }}
                            thumbYProps={{
                                style: {
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',  // Customize thumb color
                                    borderRadius: '4px',
                                    width: '4px'
                                }
                            }}
                            onMouseEnter={(e) => {
                                // Make track and thumb fully visible on hover
                                const track = e.target.querySelector('.ScrollbarsCustom-TrackY');
                                if (track) track.style.opacity = '1';
                            }}
                            onMouseLeave={(e) => {
                                // Revert track and thumb opacity when mouse leaves
                                const track = e.target.querySelector('.ScrollbarsCustom-TrackY');
                                if (track) track.style.opacity = '0.3';
                            }}
                        >
                            <List sx={{ padding: 0, margin: 0 }}>
                                {Object.entries(menuItems).map(([key, item]) => (
                                    <ListItem key={key}>
                                        <ButtonBase component="a"
                                            href={item.path} sx={{ width: '100%' }}>
                                            <ListItemIcon sx={{
                                                // color: "text.primary"
                                            }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText primary={item.label} />
                                        </ButtonBase>
                                    </ListItem>
                                ))}
                            </List>
                        </Scrollbar>
                    </Box>
                    {/* Footer or additional section */}
                    <Box
                        sx={{
                            textAlign: 'center',
                            width: '100%',
                            mt: 'auto',
                            p: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            // backgroundColor: 'primary.main', // Change to your preferred color
                            color: 'text.primary',
                            // borderTop: '1px solid #e0e0e0', // Adds a subtle top border
                            boxShadow: '0px -1px 6px rgba(0, 0, 0, 0.1)', // Subtle shadow for depth
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                whiteSpace: 'nowrap',
                                fontWeight: 800,
                                fontSize: '0.987rem',
                                color: "primary.dark"
                            }}
                        >
                            © {new Date().getFullYear()} Glassyfied
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Toggle Button */}
            <IconButton
                size='small'
                onClick={togglePanel}
                sx={{
                    position: 'absolute', // Fix the button positioning
                    left: sidePanelOpen ? '204px' : '-12px', // Adjust left position when panel is open or closed
                    transition: 'all 0.5s ease', // Smooth transition for the button
                    zIndex: "tooltip", // Ensure button is on top
                    top: 8, // Adjust top spacing
                    maxWidth: '32px', // Ensure the button size is constrained
                    maxHeight: '32px', // Adjust height to be smaller
                    backgroundColor: "background.default",
                    // boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // Light shadow for depth
                    borderRadius: '50%',
                    '&:hover': {
                        backgroundColor: "primary.main", // Highlight with primary color on hover
                        // color: "common.white", // Ensure icon color matches the hover state
                        transform: 'scale(1.1)', // Slight scale up effect for hover
                        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
                        color: "primary.contrastText"
                    },
                }}
            >
                {sidePanelOpen ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
        </Box>
    );
};

export default SidePanel;
